/**
 * ExplorerProvider — Provider genérico para explorers compatibles con Etherscan
 *
 * Funciona con: BSCScan, Etherscan, Polygonscan, Arbiscan, Optimism Etherscan
 * Todas las peticiones pasan por el servidor proxy (/api/proxy/explorer?network=X)
 * para mantener las API Keys fuera del browser.
 */

import { Transaction } from '@/types/transaction.types';
import { BaseTransactionProvider } from './base-provider';
import { getApiBaseUrl } from '@/config/app';

interface ExplorerTx {
  hash: string;
  from: string;
  to: string;
  value: string;
  blockNumber: string;
  timeStamp: string;
  isError: string;
  gasUsed: string;
  gasPrice: string;
  contractAddress?: string;
  tokenSymbol?: string;
  tokenDecimal?: string;
}

export class ExplorerProvider extends BaseTransactionProvider {
  /** Ruta del proxy en nuestro servidor Express */
  private readonly proxyUrl = `${getApiBaseUrl()}/api/proxy/explorer`;

  constructor(network: string, timeout = 15_000) {
    super(network, timeout);
  }

  // ----------------------------------------------------------------
  // API Pública
  // ----------------------------------------------------------------

  async getTransactions(address: string, fromBlock = 0): Promise<Transaction[]> {
    console.log(`[Explorer-${this.network}] Obteniendo transacciones para ${address}`);

    const [normalTxs, tokenTxs] = await Promise.all([
      this.fetchList('account', 'txlist', address, fromBlock),
      this.fetchList('account', 'tokentx', address, fromBlock),
    ]);

    const all = [...normalTxs, ...tokenTxs].sort((a, b) => b.timestamp - a.timestamp);
    console.log(`[Explorer-${this.network}] ${all.length} transacciones obtenidas`);
    return all;
  }

  async getTokenTransfers(address: string, _contractAddress?: string): Promise<Transaction[]> {
    return this.fetchList('account', 'tokentx', address, 0);
  }

  async getTransactionDetails(txHash: string): Promise<Transaction | null> {
    try {
      const url = this.buildUrl({ module: 'proxy', action: 'eth_getTransactionReceipt', txhash: txHash });
      const res = await fetch(url, { signal: AbortSignal.timeout(this.timeout) });
      if (!res.ok) return null;

      const data = await res.json();
      const receipt = data.result;
      if (!receipt) return null; // Aún pendiente

      const status = receipt.status === '0x1' ? 'confirmed' : 'failed';
      const blockNumber = receipt.blockNumber ? parseInt(receipt.blockNumber, 16) : 0;

      return {
        hash: txHash,
        from: receipt.from ?? '',
        to: receipt.to ?? '',
        value: '0',
        timestamp: Math.floor(Date.now() / 1000),
        blockNumber,
        status,
        type: 'send',
        network: this.network,
        source: 'explorer_sync',
      };
    } catch {
      return null;
    }
  }

  // ----------------------------------------------------------------
  // Helpers privados
  // ----------------------------------------------------------------

  /** Construye la URL del proxy con los parámetros */
  private buildUrl(params: Record<string, string | number>): string {
    const qs = new URLSearchParams({
      network: this.network,
      ...Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)])),
    }).toString();
    return `${this.proxyUrl}?${qs}`;
  }

  /** Llama al proxy y devuelve las transacciones parseadas */
  private async fetchList(
    module: string,
    action: string,
    address: string,
    startBlock: number
  ): Promise<Transaction[]> {
    try {
      const url = this.buildUrl({
        module,
        action,
        address,
        startblock: startBlock,
        endblock: 99_999_999,
        sort: 'desc',
      });

      const res = await fetch(url, { signal: AbortSignal.timeout(this.timeout) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.status !== '1') {
        // "No transactions found" no es un error real
        if (data.message === 'No transactions found') return [];
        throw new Error(data.message ?? 'Error en Explorer Proxy');
      }

      return (data.result as ExplorerTx[]).map((tx) =>
        action === 'tokentx' ? this.parseTokenTx(tx, address) : this.parseNormalTx(tx, address)
      );
    } catch (err: any) {
      if (err.name === 'TimeoutError' || err.name === 'AbortError') {
        console.warn(`[Explorer-${this.network}] Timeout en ${action}`);
      } else {
        console.error(`[Explorer-${this.network}] Error en ${action}:`, err.message);
      }
      return [];
    }
  }

  private parseNormalTx(tx: ExplorerTx, userAddress: string): Transaction {
    const isOutgoing = tx.from.toLowerCase() === userAddress.toLowerCase();
    return {
      hash: tx.hash,
      from: tx.from,
      to: tx.to,
      value: this.parseValue(tx.value, 18), // Monedas nativas siempre 18
      blockNumber: parseInt(tx.blockNumber),
      timestamp: parseInt(tx.timeStamp),
      status: tx.isError === '0' ? 'confirmed' : 'failed',
      type: isOutgoing ? 'send' : 'receive',
      network: this.network,
      gasUsed: tx.gasUsed,
      gasPrice: tx.gasPrice,
      source: 'explorer_sync',
    };
  }

  private parseTokenTx(tx: ExplorerTx, userAddress: string): Transaction {
    const isOutgoing = tx.from.toLowerCase() === userAddress.toLowerCase();
    const decimals = tx.tokenDecimal ? parseInt(tx.tokenDecimal) : 18;
    
    return {
      hash: tx.hash,
      from: tx.from,
      to: tx.to,
      value: this.parseValue(tx.value, decimals), // Usar decimales del token
      blockNumber: parseInt(tx.blockNumber),
      timestamp: parseInt(tx.timeStamp),
      status: 'confirmed',
      type: isOutgoing ? 'send' : 'receive',
      network: this.network,
      tokenAddress: tx.contractAddress,
      tokenSymbol: tx.tokenSymbol,
      tokenDecimals: decimals,
      source: 'explorer_sync',
    };
  }
}
